if ( typeof CartPairing !== 'function' ) {

  /**
   * Answers already given by the inventory API, keyed by product + location +
   * date. Adding a spread re-renders the whole cart form, this block included,
   * and without this the surviving rows would blink out and back in while the
   * same questions were asked again.
   */
  const availabilityCache = new Map();
  const AVAILABILITY_TTL = 60000;

  const cacheKey = (productId, locationId, dateIso) => `${productId}|${locationId}|${dateIso}`;

  const readCache = (key) => {
    const hit = availabilityCache.get(key);
    if ( ! hit ) return null;
    if ( Date.now() - hit.at > AVAILABILITY_TTL ) {
      availabilityCache.delete(key);
      return null;
    }
    return hit;
  };

  /**
   * Reveals the cart's spread upsell, one product at a time.
   *
   * The block arrives from the server hidden: bread is in the cart and these
   * spreads are not, but nobody has asked yet whether they can actually be made
   * for the selected date. This element asks, and shows only what comes back
   * available. Anything it cannot confirm is dropped — the same check runs again
   * at checkout, and an upsell that gets rejected there is worse than no upsell.
   */
  class CartPairing extends HTMLElement {

    connectedCallback() {
      this._onDeliveryConfigSaved = () => {
        availabilityCache.clear();
        this._check();
      };
      document.addEventListener('deliveryConfigSaved', this._onDeliveryConfigSaved);
      if ( ! this._bound ) {
        this._bound = true;
        this.addEventListener('click', this._onClick.bind(this));
      }
      this._check();
    }

    disconnectedCallback() {
      document.removeEventListener('deliveryConfigSaved', this._onDeliveryConfigSaved);
    }

    async _check() {
      const items = Array.from(this.querySelectorAll('[data-pairing-item]'));
      if ( ! items.length ) return;

      const config = this._loadConfig();
      const filter = this._sharedFilter();

      // Without a delivery config there is no date to check against, so the
      // theme's own stock state — which already excluded sold out products — is
      // the best answer available.
      if ( ! config || ! config.locationId || ! config.date || ! filter ) {
        this._reveal(items);
        return;
      }

      const dateIso = this._resolveDate(config.date);

      // Whatever was already confirmed shows up right away; only the unknowns
      // wait on the network.
      const unknown = [];
      const confirmed = [];

      items.forEach((item) => {
        const cached = readCache(cacheKey(item.dataset.productId, config.locationId, dateIso));
        if ( cached === null ) {
          unknown.push(item);
        } else if ( cached.available ) {
          confirmed.push(item);
        } else {
          this._markUnavailable(item);
        }
      });

      this._reveal(confirmed);

      const answered = await Promise.all(unknown.map(async (item) => {
        const productId = parseInt(item.dataset.productId, 10);
        let available = false;
        try {
          const data = await filter.getProductAvailability(productId, config.locationId, dateIso);
          available = this._isAvailable(data, dateIso);
          availabilityCache.set(cacheKey(productId, config.locationId, dateIso), { available, at: Date.now() });
        } catch (_) {
          available = false;
        }
        if ( ! available ) this._markUnavailable(item);
        return available ? item : null;
      }));

      this._reveal(answered.filter(Boolean));
    }

    /**
     * Reads the same fields, in the same order, as the badges on /pages/order —
     * `available_quantity` is stock on hand, `is_available_today` is whether the
     * product can be had that day, and for anything made to order those two
     * disagree. An unrecognized date falls back to today, exactly as the order
     * form does, so the cart can never contradict the page the shopper just
     * came from.
     */
    _isAvailable(data, dateIso) {
      return dateIso === this._toMiamiDate(1)
        ? !! data.is_available_tomorrow
        : !! data.is_available_today;
    }

    /**
     * Shopify can refuse an add that the backoffice considers perfectly in
     * stock — its counters drift. The shopper should hear that from the row
     * they clicked, not from a button that quietly springs back.
     */
    _showAddError(button) {
      const row = button.closest('[data-pairing-item]');
      if ( ! row || row.querySelector('[data-pairing-error]') ) return;

      const message = document.createElement('span');
      message.className = 'cart-pairing__error';
      message.setAttribute('data-pairing-error', '');
      message.textContent = KROWN.settings.locales.cart_general_error;
      row.querySelector('.cart-pairing__info').appendChild(message);
    }

    _markUnavailable(item) {
      item.hidden = true;
      item.setAttribute('data-pairing-unavailable', '');
    }

    _reveal(items) {
      if ( ! items.length ) return;

      const limit = parseInt(this.dataset.pairingLimit, 10) || items.length;
      let shown = this.querySelectorAll('[data-pairing-item]:not([hidden])').length;

      items.forEach((item) => {
        if ( shown >= limit ) return;
        item.hidden = false;
        shown += 1;
      });

      if ( shown > 0 ) this.hidden = false;
    }

    async _onClick(e) {
      const button = e.target.closest('[data-pairing-add]');
      if ( ! button || button.disabled ) return;

      e.preventDefault();
      button.disabled = true;
      button.classList.add('cart-pairing__add--busy');

      try {
        const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }] }),
        });
        if ( ! response.ok ) throw new Error(`Add to cart failed: ${response.status}`);

        // Re-renders the whole cart form, this element included: the spread just
        // added drops out of the offer and the remaining ones are re-checked.
        await this._refreshCart();
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.classList.remove('cart-pairing__add--busy');
        this._showAddError(button);
      }
    }

    async _refreshCart() {
      const response = await fetch('?section_id=helper-cart');
      const text     = await response.text();
      const section  = new DOMParser().parseFromString(text, 'text/html');

      const cartItems = document.getElementById('AjaxCartForm');
      if ( cartItems ) {
        cartItems.innerHTML = section.getElementById('AjaxCartForm').innerHTML;
        cartItems.ajaxifyCartItems();
      }

      const cartSubtotal = document.getElementById('AjaxCartSubtotal');
      if ( cartSubtotal ) {
        cartSubtotal.innerHTML = section.getElementById('AjaxCartSubtotal').innerHTML;
      }

      document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
        elm.textContent = section.querySelector('[data-cart-count]').textContent;
      });
      document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
        elm.textContent = section.querySelector('[data-cart-total]').textContent;
      });
    }

    _toMiamiDate(offsetDays) {
      const now   = new Date();
      const miami = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      miami.setDate(miami.getDate() + offsetDays);
      const pad = (n) => String(n).padStart(2, '0');
      return `${miami.getFullYear()}-${pad(miami.getMonth() + 1)}-${pad(miami.getDate())}`;
    }

    _resolveDate(date) {
      if ( date === 'today' )    return this._toMiamiDate(0);
      if ( date === 'tomorrow' ) return this._toMiamiDate(1);
      return date;
    }

    _sharedFilter() {
      if ( ! window.InventoryFilter ) return null;
      if ( ! window._sharedInventoryFilter ) {
        window._sharedInventoryFilter = new window.InventoryFilter();
      }
      return window._sharedInventoryFilter;
    }

    _loadConfig() {
      try {
        const raw = JSON.parse(localStorage.getItem('domaselo_delivery_config'));
        if ( ! raw ) return null;

        // Normalize legacy symbolic strings and stale past dates → today
        const todayIso = this._toMiamiDate(0);
        const d = raw.date;
        if ( ! d || d === 'today' || d === 'tomorrow' || d < todayIso ) {
          const normalized = Object.assign({}, raw, { date: todayIso });
          delete normalized.autoSwitchedFromToday;
          try { localStorage.setItem('domaselo_delivery_config', JSON.stringify(normalized)); } catch (_) {}
          return normalized;
        }
        return raw;
      } catch (_) {
        return null;
      }
    }

  }

  if ( typeof customElements.get('cart-pairing') === 'undefined' ) {
    customElements.define('cart-pairing', CartPairing);
  }

}
